import React, { useState, useEffect } from 'react';
import {
  Select, Input, Button, Form, Spin, message, Descriptions, Card, Divider, Typography, Modal, Row, Col, Space, Steps
} from 'antd';
import {
  WalletOutlined, MobileOutlined, BankOutlined, SafetyOutlined,
  CreditCardOutlined, ArrowLeftOutlined
} from '@ant-design/icons';
import { getRequest, postRequest } from '../../Utils/AppApi';
import {
  INSTANTPAY_CATEGORIES,
  INSTANTPAY_BILLERS,
  INSTANTPAY_BILLER_DETAILS,
  INSTANTPAY_PRE_PAYMENT_ENQUIRY,
  INSTANTPAY_MAKE_PAYMENT
} from '../../Utils/appConstants';
import { useHistory } from "react-router-dom";
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import Invoice from './invoice';

const { Option } = Select;
const { Title, Text } = Typography;
const { Step } = Steps;

const PRIMARY_COLOR = '#C9262C';
const ACCENT_COLOR = '#FFDD09';

const safeNumber = (value, defaultValue = 0) => {
  const num = parseFloat(value);
  return isNaN(num) ? defaultValue : num;
};

const CommonBillPayment = ({ category, restrictToBiller = null }) => {
  const history = useHistory();

  // States
  const [categories, setCategories] = useState([]);
  const [billers, setBillers] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedBiller, setSelectedBiller] = useState(null);
  const [loading, setLoading] = useState(false);
  const [paying, setPaying] = useState(false);
  const [billData, setBillData] = useState(null);
  const [bbpsTxId, setBbpsTxId] = useState(null);
  const [fetchTransactionId, setFetchTransactionId] = useState(null);
  const [extraFields, setExtraFields] = useState([]);
  const [extraValues, setExtraValues] = useState({});
  const [currentStep, setCurrentStep] = useState(0);
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const [receiptData, setReceiptData] = useState(null);
  const [partialAmount, setPartialAmount] = useState(null);
  const [customAmountInput, setCustomAmountInput] = useState('');
  const [enquiryReferenceId, setEnquiryReferenceId] = useState(null);
  const [billerDetails, setBillerDetails] = useState(null);
  const [pageNumber, setPageNumber] = useState(1);
  const [hasMoreBillers, setHasMoreBillers] = useState(true);

  // Fetch categories on mount
  useEffect(() => {
    fetchCategories();
  }, []);

  // Fetch categories from InstantPay
  const fetchCategories = async () => {
    setLoading(true);
    try {
      const response = await getRequest(INSTANTPAY_CATEGORIES, {}, history);
      if (response.success && response.data) {
        let categoriesData = response.data;
        
        // If category prop is provided, filter to only that category
        if (category) {
          categoriesData = categoriesData.filter(
            cat => cat.categoryName.toLowerCase() === category.toLowerCase() ||
                   cat.categoryKey === category
          );
        }
        
        setCategories(categoriesData);
        
        // Auto-select category if passed as prop and only one category exists
        if (category && categoriesData.length > 0) {
          const matchedCategory = categoriesData[0];
          setSelectedCategory(matchedCategory);
          await fetchBillers(matchedCategory.categoryKey);
        }
      } else {
        // toast.error(response.message || 'Failed to fetch categories');
      }
    } catch (err) {
      // toast.error(err.message || 'Error fetching categories');
    } finally {
      setLoading(false);
    }
  };

  // Fetch billers for selected category
  const fetchBillers = async (categoryKey, page = 1, append = false) => {
    setLoading(true);
    try {
      const response = await postRequest(INSTANTPAY_BILLERS, {
        categoryKey: categoryKey,
        pageNumber: page,
        recordsPerPage: 20
      }, history);

      if (response.success && response.data?.records) {
        let newBillers = response.data.records;
        
        // Filter out blocked billers
        newBillers = newBillers.filter(biller => 
          biller.billerStatus !== 'BLOCKED' && biller.isAvailable === true
        );
        
        // If restrictToBiller is provided, filter to only that biller
        if (restrictToBiller) {
          newBillers = newBillers.filter(biller => 
            biller.billerName.toLowerCase().includes(restrictToBiller.toLowerCase()) ||
            biller.billerId === restrictToBiller
          );
        }
        
        if (append) {
          setBillers(prev => [...prev, ...newBillers]);
        } else {
          setBillers(newBillers);
          
          // Auto-select biller if only one is available and not already selected
          if (newBillers.length === 1 && !selectedBiller) {
            const singleBiller = newBillers[0];
            setSelectedBiller(singleBiller);
            await fetchBillerDetails(singleBiller.billerId);
          }
        }

        const totalPages = response.data.meta?.totalPages || 1;
        setHasMoreBillers(page < totalPages);
        setPageNumber(page);
      } else {
        if (!append) setBillers([]);
        toast.error(response.message || 'Failed to fetch billers');
      }
    } catch (err) {
      toast.error(err.message || 'Error fetching billers');
      if (!append) setBillers([]);
    } finally {
      setLoading(false);
    }
  };

  // Load more billers
  const loadMoreBillers = () => {
    if (hasMoreBillers && selectedCategory) {
      fetchBillers(selectedCategory.categoryKey, pageNumber + 1, true);
    }
  };

  // Handle category selection
  const handleCategoryChange = async (categoryKey) => {
    const category = categories.find(cat => cat.categoryKey === categoryKey);
    setSelectedCategory(category);
    setSelectedBiller(null);
    setBillData(null);
    setBbpsTxId(null);
    setFetchTransactionId(null);
    setExtraFields([]);
    setExtraValues({});
    setCurrentStep(0);
    setReceiptData(null);
    setBillers([]);
    setPageNumber(1);
    setHasMoreBillers(true);

    if (categoryKey) {
      await fetchBillers(categoryKey);
    }
  };

  // Fetch biller details to get required parameters
  const fetchBillerDetails = async (billerId) => {
    try {
      const response = await postRequest(INSTANTPAY_BILLER_DETAILS, { billerId }, history);
      if (response.success && response.data) {
        const details = response.data;
        setBillerDetails(details);

        // Build dynamic fields from parameters
        if (details.parameters && Array.isArray(details.parameters)) {
          const fields = details.parameters.map((param, idx) => ({
            key: `param${idx + 1}`,
            label: param.desc || param.name,
            required: param.mandatory === 1,
            minLength: param.minLength,
            maxLength: param.maxLength,
            inputType: param.inputType,
            regex: param.regex,
            placeholder: `Enter ${param.desc || param.name}`
          }));
          setExtraFields(fields);

          const initialValues = {};
          fields.forEach(field => { initialValues[field.key] = ''; });
          setExtraValues(initialValues);
        }
      }
    } catch (err) {
      console.error('Error fetching biller details:', err);
      toast.error('Could not load biller details');
    }
  };

  // Handle biller selection
  const handleBillerChange = async (billerId) => {
    const biller = billers.find(b => b.billerId === billerId);
    setSelectedBiller(biller);
    setBillData(null);
    setBbpsTxId(null);
    setFetchTransactionId(null);
    setExtraFields([]);
    setExtraValues({});
    setCurrentStep(0);
    setReceiptData(null);
    setPartialAmount(null);
    setCustomAmountInput('');
    setEnquiryReferenceId(null);

    if (billerId) {
      await fetchBillerDetails(billerId);
    }
  };

  const handleExtraFieldChange = (key, value) => {
    setExtraValues(prev => ({ ...prev, [key]: value }));
  };

  // Fetch bill using Pre-Payment Enquiry
  const handleFetchBill = async () => {
    if (!selectedBiller) return toast.error('Please select a biller');

    // Validate required fields
    for (const field of extraFields) {
      if (field.required) {
        const value = extraValues[field.key];
        if (!value || value.trim() === '') {
          return toast.error(`${field.label} is required`);
        }
      }
    }

    setLoading(true);
    try {
      // Build input parameters
      const inputParameters = {};
      extraFields.forEach((field, idx) => {
        inputParameters[`param${idx + 1}`] = extraValues[field.key] || '';
      });

      const payload = {
        billerId: selectedBiller.billerId,
        inputParameters: inputParameters,
        externalRef: `REF_${Date.now()}`,
        transactionAmount: "0",
        category: selectedCategory?.categoryName
      };

      const response = await postRequest(INSTANTPAY_PRE_PAYMENT_ENQUIRY, payload, history);

      if (response.success && response.data) {
        const billDetails = {
          customerName: response.data.CustomerName || 'N/A',
          billedamount: response.data.BillAmount || 0,
          payamount: response.data.BillAmount || 0,
          dueDate: response.data.BillDueDate || 'N/A',
          billdate: response.data.BillDate || 'N/A',
          billNumber: response.data.BillNumber || 'N/A',
          billPeriod: response.data.BillPeriod || 'N/A',
          enquiryReferenceId: response.data.enquiryReferenceId,
          partPayment: false
        };

        const commissionData = response.commissionData || {};
        billDetails.serviceAmount = commissionData.serviceAmount || 0;
        billDetails.commissionAmount = commissionData.commissionAmount || 0;
        billDetails.tdsAmount = commissionData.tdsAmount || 0;

        const txId = response.bbpsTxId;
        const fetchTxId = response.fetchTransactionId;
        const enquiryRefId = response.enquiryReferenceId;

        setBillData(billDetails);
        setBbpsTxId(txId);
        setFetchTransactionId(fetchTxId);
        setEnquiryReferenceId(enquiryRefId);
        setPartialAmount(null);
        setCustomAmountInput('');
        setCurrentStep(1);
        toast.success('Bill fetched successfully');
      } else {
        toast.error(response.message || 'Failed to fetch bill');
      }
    } catch (err) {
      toast.error(err.message || 'Error fetching bill');
    } finally {
      setLoading(false);
    }
  };

  const handlePartialAmountChange = (e) => {
    let value = e.target.value;
    if (value === '') {
      setCustomAmountInput('');
      setPartialAmount(null);
      return;
    }
    let num = parseFloat(value);
    if (isNaN(num) || num < 0) return;
    setCustomAmountInput(num.toString());
    setPartialAmount(num);
  };

  const executeBillPayment = async () => {
    if (!billData) {
      toast.error('No bill data to pay');
      setIsConfirmModalOpen(false);
      return;
    }
    if (!bbpsTxId) {
      toast.error('Transaction ID missing. Please fetch bill again.');
      setIsConfirmModalOpen(false);
      return;
    }

    setPaying(true);
    try {
      const orderId = fetchTransactionId;
      const defaultAmount = safeNumber(billData.payamount, safeNumber(billData.billedamount));
      let amountToPay = defaultAmount;
      if (billData.partPayment && partialAmount !== null && partialAmount > 0) {
        amountToPay = partialAmount;
      }

      // Build input parameters
      const inputParameters = {};
      extraFields.forEach((field, idx) => {
        inputParameters[`param${idx + 1}`] = extraValues[field.key] || '';
      });

      const payload = {
        bbpsTxId: bbpsTxId,
        billerId: selectedBiller.billerId,
        inputParameters: inputParameters,
        transactionAmount: amountToPay,
        externalRef: orderId,
        enquiryReferenceId: enquiryReferenceId,
        category: selectedCategory?.categoryName
      };

      const response = await postRequest(INSTANTPAY_MAKE_PAYMENT, payload, history);

      if (response.success) {
        const result = response.data;

        const receipt = {
          txnStatus: 'PAYMENT_SUCCESS',
          customerPhoneNumber: result.billerDetails?.account || '',
          category: selectedCategory?.categoryName,
          txtId: orderId,
          apiProvider: 'IP',
          apiResponse: {
            additionalParams: {
              txnReferenceId: result.txnReferenceId,
              billerReferenceNumber: result.poolReferenceId
            }
          },
          apiRequest: { timeStamp: new Date().toISOString() },
          billerName: result.billerDetails?.name || selectedBiller.billerName,
          accountNumber: result.billerDetails?.account || extraValues.param1 || '',
          paidAmount: amountToPay,
          transactionId: fetchTransactionId,
          poolReferenceId: result.poolReferenceId,
          txnReferenceId: result.txnReferenceId
        };
        setReceiptData(receipt);
        setCurrentStep(2);
        toast.success(response.message || 'Payment successful!');
      } else if (response.statuscode === 'PENDING') {
        toast.warning('Transaction is pending. Please check status later.');
      } else {
        toast.error(response.message || response.status || 'Payment failed');
      }
    } catch (err) {
      toast.error(err.message || 'Error processing payment');
    } finally {
      setPaying(false);
      setIsConfirmModalOpen(false);
    }
  };

  const confirmPayment = () => {
    if (!billData) return toast.error('Please fetch bill first');
    setIsConfirmModalOpen(true);
  };

  const handleConfirmOk = () => {
    executeBillPayment();
  };

  const handleConfirmCancel = () => {
    setIsConfirmModalOpen(false);
  };

  const handleBackToStep1 = () => {
    setCurrentStep(0);
    setBillData(null);
    setReceiptData(null);
    setPartialAmount(null);
    setCustomAmountInput('');
    setIsConfirmModalOpen(false);
  };

  const handleNewPayment = () => {
    setSelectedCategory(null);
    setSelectedBiller(null);
    setBillData(null);
    setBbpsTxId(null);
    setFetchTransactionId(null);
    setExtraFields([]);
    setExtraValues({});
    setReceiptData(null);
    setPartialAmount(null);
    setCustomAmountInput('');
    setCurrentStep(0);
    setIsConfirmModalOpen(false);
    setEnquiryReferenceId(null);
    setBillerDetails(null);
    setBillers([]);
    setPageNumber(1);
    setHasMoreBillers(true);
  };

  // Render Step 1 (Category and Biller Selection)
  const renderStep1 = () => (
    <Form layout="vertical" size="small" style={{ marginTop: 0 }}>
      <Row gutter={[12, 8]}>
        {/* Only show category selector if no category prop is provided */}
        {!category && (
          <Col xs={24}>
            <Form.Item label="Select Category" required style={{ marginBottom: 8 }}>
              <Select
                placeholder="Choose bill category"
                onChange={handleCategoryChange}
                value={selectedCategory?.categoryKey}
                loading={loading}
                showSearch
                optionFilterProp="children"
                size="middle"
                style={{ borderRadius: 8 }}
              >
                {categories.map(cat => (
                  <Option key={cat.categoryKey} value={cat.categoryKey}>
                    {cat.categoryName}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
        )}

        {/* Show selected category as read-only text if category prop is provided */}
        {category && selectedCategory && (
          <Col xs={24}>
            <Form.Item label="Category" style={{ marginBottom: 8 }}>
              <Input 
                value={selectedCategory?.categoryName} 
                disabled 
                size="middle"
                style={{ borderRadius: 8, background: '#f5f5f5' }}
              />
            </Form.Item>
          </Col>
        )}

        {/* Only show biller selector if no restrictToBiller prop or if multiple billers */}
        {(!restrictToBiller || billers.length > 1) && selectedCategory && (
          <Col xs={24}>
            <Form.Item label="Select Biller" required style={{ marginBottom: 8 }}>
              <Select
                placeholder="Choose your biller"
                onChange={handleBillerChange}
                value={selectedBiller?.billerId}
                loading={loading}
                showSearch
                optionFilterProp="children"
                size="middle"
                style={{ borderRadius: 8 }}
                dropdownRender={(menu) => (
                  <div>
                    {menu}
                    {hasMoreBillers && (
                      <div style={{ padding: '8px', textAlign: 'center' }}>
                        <Button
                          type="link"
                          onClick={loadMoreBillers}
                          loading={loading}
                          size="small"
                        >
                          Load More
                        </Button>
                      </div>
                    )}
                  </div>
                )}
              >
                {billers.map(biller => (
                  <Option key={biller.billerId} value={biller.billerId}>
                    {biller.billerName}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
        )}

        {/* Show selected biller as read-only text if restrictToBiller is provided */}
        {restrictToBiller && selectedBiller && billers.length === 1 && (
          <Col xs={24}>
            <Form.Item label="Biller" style={{ marginBottom: 8 }}>
              <Input 
                value={selectedBiller?.billerName} 
                disabled 
                size="middle"
                style={{ borderRadius: 8, background: '#f5f5f5' }}
              />
            </Form.Item>
          </Col>
        )}

        {selectedBiller && extraFields.length > 0 && (
          <Col xs={24}>
            <Divider orientation="left" style={{ margin: '8px 0', fontSize: 12 }}>
              Customer Information
            </Divider>
            <Row gutter={[12, 8]}>
              {extraFields.map(field => (
                <Col xs={24} md={12} key={field.key}>
                  <Form.Item
                    label={field.label}
                    required={field.required}
                    style={{ marginBottom: 8 }}
                    tooltip={field.required ? "Required field" : "Optional"}
                  >
                    <Input
                      value={extraValues[field.key] || ''}
                      onChange={e => handleExtraFieldChange(field.key, e.target.value)}
                      placeholder={field.placeholder}
                      size="middle"
                      style={{ borderRadius: 8 }}
                      maxLength={field.maxLength}
                    />
                  </Form.Item>
                </Col>
              ))}
            </Row>
          </Col>
        )}

        {selectedBiller && (
          <Col xs={24}>
            <Button
              type="primary"
              onClick={handleFetchBill}
              loading={loading}
              size="middle"
              block
              icon={<WalletOutlined />}
              style={{
                borderRadius: 40,
                height: 40,
                fontWeight: 500,
                background: PRIMARY_COLOR,
                border: 'none'
              }}
            >
              Fetch Bill
            </Button>
          </Col>
        )}
      </Row>
    </Form>
  );

  // Render Step 2 (Bill Summary and Payment)
  const renderStep2 = () => {
    if (!billData) {
      return (
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <Spin size="large" tip="Loading bill details..." />
        </div>
      );
    }

    const totalAmount = safeNumber(billData.billedamount);
    const defaultPayable = safeNumber(billData.payamount, totalAmount);
    const finalAmount = partialAmount !== null ? safeNumber(partialAmount) : defaultPayable;

    return (
      <>
        <Card
          title="Bill Summary"
          size="small"
          style={{ marginBottom: 12, borderRadius: 12 }}
          headStyle={{ borderBottom: '1px solid #f0f0f0', fontWeight: 500, fontSize: 14, padding: '8px 12px' }}
          bodyStyle={{ padding: '12px' }}
        >
          <Descriptions column={1} bordered size="small">
            <Descriptions.Item label="Customer Name" labelStyle={{ fontSize: 12 }} contentStyle={{ fontSize: 12 }}>
              {billData.customerName || 'N/A'}
            </Descriptions.Item>
            <Descriptions.Item label="Bill Number" labelStyle={{ fontSize: 12 }} contentStyle={{ fontSize: 12 }}>
              {billData.billNumber || 'N/A'}
            </Descriptions.Item>
            <Descriptions.Item label="Bill Period" labelStyle={{ fontSize: 12 }} contentStyle={{ fontSize: 12 }}>
              {billData.billPeriod || 'N/A'}
            </Descriptions.Item>
            <Descriptions.Item label="Billed Amount" labelStyle={{ fontSize: 12 }} contentStyle={{ fontSize: 12, fontWeight: 500 }}>
              ₹{totalAmount.toFixed(2)}
            </Descriptions.Item>
            <Descriptions.Item label="Due Date" labelStyle={{ fontSize: 12 }} contentStyle={{ fontSize: 12 }}>
              {billData.dueDate || 'N/A'}
            </Descriptions.Item>
            <Descriptions.Item label="Bill Date" labelStyle={{ fontSize: 12 }} contentStyle={{ fontSize: 12 }}>
              {billData.billdate || 'N/A'}
            </Descriptions.Item>
          </Descriptions>

          {billData.partPayment && (
            <div style={{ marginTop: 12 }}>
              <Form.Item label="Enter Payment Amount (₹)" style={{ marginBottom: 8 }}>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  value={customAmountInput}
                  onChange={handlePartialAmountChange}
                  prefix="₹"
                  size="middle"
                  style={{ borderRadius: 8 }}
                />
                <Text type="secondary" style={{ fontSize: 11 }}>Enter any positive amount</Text>
              </Form.Item>
            </div>
          )}
        </Card>

        <Row gutter={12}>
          <Col span={12}>
            <Button
              icon={<ArrowLeftOutlined />}
              onClick={handleBackToStep1}
              size="middle"
              block
              style={{
                borderRadius: 40,
                height: 40,
                borderColor: PRIMARY_COLOR,
                color: PRIMARY_COLOR,
                background: 'white'
              }}
            >
              Back
            </Button>
          </Col>
          <Col span={12}>
            <Button
              type="primary"
              onClick={confirmPayment}
              loading={paying}
              size="middle"
              block
              icon={<WalletOutlined />}
              style={{
                background: PRIMARY_COLOR,
                borderColor: PRIMARY_COLOR,
                borderRadius: 40,
                height: 40
              }}
            >
              Pay Now ₹{finalAmount.toFixed(2)}
            </Button>
          </Col>
        </Row>
      </>
    );
  };

  // Render Step 3 (Receipt)
  const renderStep3 = () => (
    <div>
      <Invoice transactionId={receiptData?.transactionId} apiProvider="IP" />
    </div>
  );

  const header = (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 15 }}>
      <img src="../logo_small.png" alt="Website Logo" style={{ height: 50 }} />
      <div style={{ textAlign: 'center' }}>
        <Title level={4} style={{ margin: 0, fontWeight: 600, fontSize: 20 }}>
          Pay {selectedCategory?.categoryName || category || 'Bill'}
        </Title>
        {/* <Text type="secondary" style={{ fontSize: 13 }}>InstantPay • Secure • Zero Commission</Text> */}
      </div>
      <img src="../images/bharat_connect.png" alt="BBPS Logo" style={{ height: 50 }} />
    </div>
  );

  return (
    <div style={{ maxWidth: 900, margin: '0 auto', padding: 0 }}>
      <style>{`
        .ant-steps-item-icon {
          background: transparent !important;
        }
        .ant-steps-item-icon .ant-steps-icon {
          background: transparent !important;
        }
        .ant-steps-item-process .ant-steps-item-icon {
          border-color: ${PRIMARY_COLOR} !important;
        }
        .ant-steps-item-process .ant-steps-item-icon .ant-steps-icon {
          color: ${PRIMARY_COLOR} !important;
        }
        .ant-steps-item-finish .ant-steps-item-icon {
          border-color: ${PRIMARY_COLOR} !important;
        }
        .ant-steps-item-finish .ant-steps-item-icon .ant-steps-icon {
          color: ${PRIMARY_COLOR} !important;
        }
        .ant-steps-item-finish > .ant-steps-item-container > .ant-steps-item-tail::after {
          background-color: ${PRIMARY_COLOR} !important;
        }
      `}</style>
      <Card
        bordered={false}
        style={{
          borderRadius: 20,
          boxShadow: '0 8px 20px -6px rgba(0,0,0,0.1)',
          background: 'linear-gradient(145deg, #ffffff 0%, #f8faff 100%)',
          padding: '12px 16px'
        }}
      >
        <Space direction="vertical" size="small" style={{ width: '100%' }}>
          {header}
          <Divider style={{ margin: '4px 0' }} />
          <Steps current={currentStep} size="small" style={{ marginBottom: 12 }}>
            <Step title="Select Biller" icon={<BankOutlined />} />
            <Step title="Confirm Payment" icon={<CreditCardOutlined />} />
            <Step title="Receipt" icon={<WalletOutlined />} />
          </Steps>

          {currentStep === 0 && renderStep1()}
          {currentStep === 1 && renderStep2()}
          {currentStep === 2 && renderStep3()}
        </Space>
      </Card>

      <Modal
        title={
          <Space size="small">
            <SafetyOutlined /> Confirm Payment
          </Space>
        }
        open={isConfirmModalOpen}
        onOk={handleConfirmOk}
        onCancel={handleConfirmCancel}
        okText="Yes, Pay Now"
        cancelText="Cancel"
        maskClosable={false}
        confirmLoading={paying}
      >
        <p>
          Are you sure you want to pay ₹
          {(() => {
            const amount = partialAmount !== null
              ? safeNumber(partialAmount)
              : safeNumber(billData?.payamount, safeNumber(billData?.billedamount));
            return amount.toFixed(2);
          })()}?
        </p>
        <p>Gateway: InstantPay</p>
        <p>This action cannot be undone.</p>
      </Modal>
    </div>
  );
};

export default CommonBillPayment;